/** @jsxImportSource @emotion/react */

import { Fade, List, ListItem, useMediaQuery } from '@material-ui/core';
import { Buttons, Dialogs, breakpoints } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { useMerchantData } from 'contexts';
import { PageAction } from 'enums';
import { theme } from 'global';
import { FC, useEffect, useState } from 'react';
import { logEvent } from 'utils';
import * as styles from './HowItWorks.styles';
import ProductSelect from './ProductSelect';
import {
  AllApplicableProductsInOrder,
  ApplicableProduct,
  ProductDetails,
} from './ProductTypesAndConstants';

interface HowItWorksDialogProps {
  open: boolean;
  onClose: () => void;
}

const HowItWorksDialog: FC<HowItWorksDialogProps> = (
  props: HowItWorksDialogProps
) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const getProductSet = (): ApplicableProduct[] => {
    const { merchantProducts } = useMerchantData();
    const merchantProductClassification = merchantProducts.map(
      (p) => p.productClassification
    );
    const productSet = AllApplicableProductsInOrder.filter((p) =>
      merchantProductClassification.includes(p)
    );
    return Array.from(productSet);
  };

  const applicableProducts = getProductSet();
  const [currentProduct, setCurrentProduct] =
    useState<ApplicableProduct | null>(null);

  useEffect(() => {
    if (props.open) {
      logEvent(PageAction.productsAndOffersHowItWorksDialogOpen);
    }
  }, [props.open]);

  useEffect(() => {
    if (applicableProducts.length > 0) {
      setCurrentProduct(applicableProducts[0]);
    }
  }, []);

  return currentProduct === null ? (
    <></>
  ) : (
    <Dialogs.Basic
      open={props.open}
      showClose={false}
      onClose={props.onClose}
      scroll={isMobile ? 'body' : 'paper'}
      TransitionComponent={isMobile ? Fade : undefined}
      css={[styles.dialogWithNoPadding, isMobile && styles.mobileDialog]}
    >
      <div id="dialogInfoBox" css={styles.dialogInfoBox}>
        <ProductSelect
          Current={currentProduct}
          Products={applicableProducts}
          onProductSelection={(p): void => {
            setCurrentProduct(p);
          }}
        />
        <b className="title3">
          {ProductDetails.get(currentProduct).IntroductionTitle}
        </b>
        <div css={styles.explanationBox}>
          <div css={styles.creditLimit}>
            <div className="heading2">
              {ProductDetails.get(currentProduct).CreditLimit}
            </div>
          </div>
          <List>
            {ProductDetails.get(currentProduct).Explanation.map((item, idx) => (
              <ListItem key={idx} css={styles.explanationListItem}>
                <div css={styles.explanationListItemIcon}>
                  <Icons.Check
                    width="16"
                    height="16"
                    color={theme.colors.primary.main}
                  />
                </div>
                <div css={styles.explanationListItemText}>
                  <div className="body2">{item}</div>
                </div>
              </ListItem>
            ))}
            <ListItem css={styles.explanationListItem}>
              <div css={styles.explanationListItemIcon}>
                <Icons.Check
                  width="16"
                  height="16"
                  color={theme.colors.primary.main}
                />
              </div>
              <div css={styles.explanationListItemText}>
                <div className="body2">
                  T&Cs apply, see{' '}
                  <a
                    href={ProductDetails.get(currentProduct).TermsConditions}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ProductDetails.get(currentProduct).TermsConditions}
                  </a>{' '}
                  for more
                </div>
              </div>
            </ListItem>
          </List>
        </div>
      </div>
      <div css={styles.dialogButton}>
        <Buttons.Primary onClick={props.onClose}>
          <div className="body1">Okay, got it</div>
        </Buttons.Primary>
      </div>
    </Dialogs.Basic>
  );
};

export default HowItWorksDialog;
